import { ColumnProps } from "antd/lib/table";
import { RequestHistory, RequestHistoryFilter } from "models/RequestHistory";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { LayoutCell, LayoutHeader, OneLineText } from "react3l-ui-library";
import nameof from "ts-nameof.macro";
import { filterService } from "services/page-services/filter-service";
import { Moment } from "moment";
import { formatDateTime } from "helpers/date-time";
import {
    ROLE_HISTORY_DETAIL_ROUTE,
} from "config/route-consts";
import { OrderType } from "react3l-common";
import { requestHistoryService } from "services/page-services/request-history-service";
import { useHistory } from "react-router-dom";
import { roleRepository } from "repositories/role-repository";
import { View24 } from "@carbon/icons-react";
import { AppUser } from "models/AppUser";
import { formatNumber } from "helpers/number";
import { Permission } from "models/Permission";

export function useRoleHistoryListHook() {
    const [translate] = useTranslation();
    const [isVisible, setIsVisible] = useState(false);
    const [dataHistoryPreview, setDataHistoryPreview] = useState([]);
    const [isSpinning, setIsSpinning] = useState(false);
    const { modelFilter: filter, dispatchFilter } = filterService.useModelFilter(
        RequestHistoryFilter,
        {
            orderBy: "savedAt",
            orderType: OrderType.DESC,
        }
    );

    const {
        requestHistoryList,
        requestHistoryCount,
        rowSelection,
        handleCompareHistory,
        handlePagination,
    } = requestHistoryService.useHistoryRequestList<
        RequestHistory,
        RequestHistoryFilter
    >(
        roleRepository.listRequestHistory,
        roleRepository.countRequestHistory,
        filter,
        dispatchFilter,
        ROLE_HISTORY_DETAIL_ROUTE,
        "versionId"
    );

    const history = useHistory();
    const handleGoBack = React.useCallback(() => {
        history.goBack();
    }, [history]);

    const previewHistory = React.useCallback((requestHistory: any) => {
        setIsSpinning(true);
        roleRepository.getRequestHistory(requestHistory?.versionId)
            .subscribe({
                next: (res: AppUser) => {
                    setIsVisible(true);
                    setDataHistoryPreview(res?.content?.permissions);
                    setIsSpinning(false);
                },
                error: () => {
                    setIsSpinning(false);
                },
            });
    }, [])

    const handleCloseModal = React.useCallback(() => {
        setIsVisible(false);
    }, [])

    const columns: ColumnProps<any>[] = React.useMemo(
        () => [
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("appUsers.histories.appUser")}
                    />
                ),
                key: nameof("actor"),
                dataIndex: nameof("actor"),
                ellipsis: true,
                render(actor: string) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={actor} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("appUsers.histories.action")}
                    />
                ),
                key: nameof("actionName"),
                dataIndex: nameof("actionName"),
                ellipsis: true,
                render(actionName: string) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={actionName} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("appUsers.histories.savedAt")}
                    />
                ),
                key: nameof("savedAt"),
                dataIndex: nameof("savedAt"),
                ellipsis: true,
                render(...params: [Moment, RequestHistory, number]) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={formatDateTime(params[0])} />
                        </LayoutCell>
                    );
                },
            },

            {
                title: (
                    <LayoutHeader orderType="left" title={translate("general.action")} />
                ),
                key: "action",
                dataIndex: "action",
                fixed: "right",
                width: 110,
                render(id: number, requestHistory: RequestHistory, index: number) {
                    return (
                        <div className="d-flex button-action-table missions-dxg">
                            <div className="d-flex">
                                <div className="enable-dxg">
                                    <View24 className="btn-missions-green" onClick={() => previewHistory(requestHistory)} />
                                </div>

                            </div>
                        </div>
                    );
                },
            },
        ],
        [translate, previewHistory]
    );

    const columnsModalHistoryPreview: ColumnProps<any>[] = React.useMemo(
        () => [
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("general.columns.index")}
                    />
                ),
                width: 50,
                ellipsis: true,
                key: "stt",
                dataIndex: "stt",
                render(...params: [number, Permission, number]) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText
                                value={formatNumber(params[2] + 1)}
                                countCharacters={50}
                            />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("appUsers.roles.code")}
                    />
                ),
                width: 150,
                key: "roleCode",
                dataIndex: "role",
                ellipsis: true,
                render(...params: [any, Permission, number]) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={params[1]?.role?.code} countCharacters={50} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("appUsers.roles.name")}
                    />
                ),
                width: 150,
                key: "roleName",
                dataIndex: "role",
                ellipsis: true,
                render(...params: [any, Permission, number]) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={params[1]?.role?.name} countCharacters={50} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("appUsers.roles.subSystem")}
                    />
                ),
                width: 100,
                key: "roleSubSystem",
                dataIndex: "role",
                render(...params: [any, Permission, number]) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={params[1]?.menu?.site?.name} countCharacters={50} />
                        </LayoutCell>
                    );
                },
            },
            {
                title: (
                    <LayoutHeader
                        orderType="left"
                        title={translate("appUsers.roles.permissions")}
                    />
                ),
                width: 100,
                key: "permissions",
                dataIndex: "permissions",
                render(...params: [any, Permission, number]) {
                    return (
                        <LayoutCell orderType="left" tableSize="md">
                            <OneLineText value={params[1]?.name} countCharacters={50} />
                        </LayoutCell>
                    );
                },
            },
        ],
        [translate]
    );

    return {
        translate,
        handleCompareHistory,
        handleGoBack,
        handlePagination,
        requestHistoryList,
        requestHistoryCount,
        rowSelection,
        columns,
        filter,
        columnsModalHistoryPreview,
        isVisible,
        dataHistoryPreview,
        handleCloseModal,
        isSpinning,
    };
}